import './ResponsiveDialog.css';

import cn from 'classnames';
import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { Flex } from '../Flex/Flex';
import { useFluentMountNode } from '../hooks/useFluentMountNode';
import { Text } from '../Text/Text';

export type ResponsiveDialogSize = 'sm' | 'md' | 'lg';

interface ResponsiveDialogProps {
  open: boolean;
  header: string;
  primaryAction?: JSX.Element;
  secondaryAction?: JSX.Element;
  thirdAction?: JSX.Element;
  headerAction?: JSX.Element;
  className?: string;
  /** Dialog width preset. 'sm' = 600px, 'md' = 720px (default), 'lg' = 900px */
  size?: ResponsiveDialogSize;
  dataCy?: string;
  children?: ReactNode;
}

export const ResponsiveDialog: React.FC<ResponsiveDialogProps> = (props) => {
  const {
    open,
    header,
    children,
    primaryAction,
    secondaryAction,
    thirdAction,
    className,
    size,
    headerAction,
    dataCy,
  } = props;

  const portalTarget = useFluentMountNode();

  if (!open || !portalTarget) {
    return <></>;
  }

  const dialog = (
    <div
      className="responsiveDialogBackdrop"
      role="dialog"
      aria-labelledby="responsiveDialogHeader"
      aria-describedby="responsiveDialogContent"
    >
      <div
        className={cn('responsiveDialog', size && `responsiveDialog--${size}`, className)}
        data-cy={dataCy}
      >
        <Flex className="responsiveDialogHeader">
          <Text as="h2" weight="bold" className="responsiveDialogTitle" id="responsiveDialogHeader">
            {header}
          </Text>
          {headerAction}
        </Flex>
        <div className="responsiveDialogContent" id="responsiveDialogContent">
          {children}
        </div>
        {(primaryAction || secondaryAction || thirdAction) && (
          <Flex className="responsiveDialogFooter" gap="8px" justify="flex-end">
            {thirdAction}
            {secondaryAction}
            {primaryAction}
          </Flex>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, portalTarget);
};
